// startupAnns.engine.ts

import {
    type StartupAnnConfig,
    type StartupAnnId,
    type WeightedStartupAnnConfig,
    startupAnns,
    STARTUP_ANNS_ENABLED,
} from "./startupAnns.config";

export interface StartupAnnUserState {
    impressions: number;
    dismissed: boolean;
    lastShownAt?: number;
}

export type StartupAnnStateMap = Record<StartupAnnId, StartupAnnUserState>;

const MIN_SESSION_GAP_MS = 0;

function isEligible(
    config: StartupAnnConfig,
    state: StartupAnnUserState | undefined,
    now: number,
): boolean {
    if (!config.enabled) return false;
    if (now < config.startAt || now > config.endAt) return false;

    const s = state ?? { impressions: 0, dismissed: false };

    // if (s.dismissed) return false;
    if (s.impressions >= config.maxImpressionsPerUser) return false;
    if (s.lastShownAt && now - s.lastShownAt < MIN_SESSION_GAP_MS) return false;

    return true;
}

function weightedPick<T>(items: { item: T; weight: number }[]): T | null {
    const total = items.reduce((sum, x) => sum + x.weight, 0);
    if (total <= 0) return null;

    let r = Math.random() * total;
    for (const { item, weight } of items) {
        if (r < weight) return item;
        r -= weight;
    }

    return null;
}

interface PickOptions {
    noneWeight?: number;
    now?: number;
    searchString?: string;
}

export function pickStartupAnnToShow(
    stateMap: StartupAnnStateMap,
    options: PickOptions = {},
): StartupAnnConfig | null {
    if (!STARTUP_ANNS_ENABLED) return null;

    const now = options.now ?? Date.now();
    const searchString = options.searchString ?? "";
    const params = new URLSearchParams(searchString);

    // 1. Check for URL-forced announcements
    //    We look for the *first* matching URL forced config.
    const forcedCandidate = startupAnns.find((config) => {
        if (config.mode === "url-forced") {
            const val = params.get(config.trigger.param);
            return val === config.trigger.value;
        }
        return false;
    });

    if (forcedCandidate) {
        // If we found a forced candidate, we ONLY consider it.
        // If it's not eligible (e.g. max impressions), we return null (show nothing),
        // effectively blocking other announcements.
        if (isEligible(forcedCandidate, stateMap[forcedCandidate.id], now)) {
            return forcedCandidate;
        }
        return null;
    }

    // 2. Filter eligible weighted announcements
    const eligible = startupAnns.filter(
        (config): config is WeightedStartupAnnConfig =>
            config.mode === "weighted" &&
            isEligible(config, stateMap[config.id], now),
    );

    // 3. Build weighted pool
    const pool: { item: StartupAnnConfig | null; weight: number }[] = [];

    for (const config of eligible) {
        if (config.weight > 0) {
            pool.push({ item: config, weight: config.weight });
        }
    }

    if (options.noneWeight && options.noneWeight > 0) {
        pool.push({ item: null, weight: options.noneWeight });
    }

    if (!pool.length) return null;

    return weightedPick(pool);
}

export function recordStartupAnnEvent(
    id: StartupAnnId,
    action: "shown" | "dismissed",
    stateMap: StartupAnnStateMap,
    now: number = Date.now(),
): StartupAnnStateMap {
    const prev = stateMap[id] ?? { impressions: 0, dismissed: false };

    const next: StartupAnnUserState = {
        impressions: prev.impressions + (action === "shown" ? 1 : 0),
        dismissed: action === "dismissed" ? true : prev.dismissed,
        lastShownAt: now,
    };

    return {
        ...stateMap,
        [id]: next,
    };
}
